import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
} from "@mui/material";
import * as Yup from "yup";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useFormik } from "formik";
import dayjs from "dayjs";
import BasicModal from "../../../components/BasicModal";
import FormModalActions from "../../../components/FormModalActions";
import { LARGE_FORM_MODAL_SX } from "../../../utils/formModalStyles";
import { getAllDepartment } from "../../../services/departmentService";
import { format } from "date-fns";
import { createDoctor, updateDoctor } from "../../../services/doctorService";
import { getAllUserRoles } from "../../../services/UserRoleService";
import {
  validateOrgTeamUserFields,
  touchFormikErrorFields,
  buildStaffExcludeContext,
} from "../../../utils/orgUserUniqueness";
import { useSubscriptionCapabilities } from "../../../hooks/useSubscriptionCapabilities";
import {
  filterDepartmentsForPackage,
  filterStaffRolesForPackage,
} from "../../../utils/subscriptionScopeUtils";
import { formatUserRoleOptionLabel } from "../../../utils/pathRoleUtils";

const validationSchema = Yup.object({
  name: Yup.string()
    .required("Name is required")
    .min(2, "Name should be of minimum 2 characters length"),
  email: Yup.string()
    .email("Enter a valid email")
    .required("Email is required"),
  education: Yup.string().required("Education is required"),
  specialty: Yup.string().required("Specialty is required"),
  aadhar: Yup.string()
    .required("Aadhar is required")
    .matches(/^\d{12}$/, "Aadhar should be a 12-digit number"),
  mobile: Yup.string()
    .required("Contact Number is required")
    .matches(/^\d{10}$/, "Contact Number should be a 10-digit number"),
  registrationNumber: Yup.string().required("Registration Number is required"),
  departmentId: Yup.string().required("Department ID is required"),
  password: Yup.string()
    .required("Password is required")
    .matches(
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
      "Password must be at least 8 characters, include an uppercase letter, a lowercase letter, a number, and a special character"
    ),
  joiningDate: Yup.date().required("Joining Date is required"),
  address: Yup.string().required("Address is required"),
});
const daysOfWeek = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const AddNewDoctor = ({ open, onClose, doctor_info = {} }) => {
  const { isPremiumPackage, hasPathPackageAccess } = useSubscriptionCapabilities();
  const [department_list, setDepartmentList] = useState([]);
  const [role_list, setRoleList] = useState([]);

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    setDepartmentList([]);
    try {
      const data = await getAllDepartment();
      setDepartmentList(data);
    } catch (error) {
      console.log(error);
    }
  };

  // roles list
  useEffect(() => {
    fetchUserRoles();
  }, []);

  const fetchUserRoles = async () => {
    setRoleList([]);
    try {
      const data = await getAllUserRoles();
      setRoleList(data);
    } catch (error) {
      console.log(error);
    }
  };

  const visibleDepartments = useMemo(
    () =>
      filterDepartmentsForPackage(department_list, {
        isPremium: isPremiumPackage,
        keepIds: [doctor_info?.departmentId],
      }),
    [department_list, isPremiumPackage, doctor_info?.departmentId]
  );

  const visibleRoles = useMemo(
    () =>
      filterStaffRolesForPackage(role_list, {
        isPremium: isPremiumPackage,
        hasPathPackageAccess,
        keepRoleNames: [doctor_info?.userRole],
      }),
    [role_list, isPremiumPackage, hasPathPackageAccess, doctor_info?.userRole]
  );

  const formik = useFormik({
    initialValues: {
      name: doctor_info?.["name"] || "",
      address: doctor_info?.["address"] || "",
      aadhar: doctor_info?.["aadhar"] || "",
      departmentId: doctor_info?.["departmentId"] || "",
      education: doctor_info?.["education"] || "",
      password: doctor_info?.["password"] || "",
      email: doctor_info?.["email"] || "",
      joiningDate: doctor_info?.["joiningDate"]
        ? dayjs(doctor_info["joiningDate"])
        : null,
      mobile: doctor_info?.["mobile"] || "",
      registrationNumber: doctor_info?.["registrationNumber"] || "",
      specialty: doctor_info?.["specialty"] || "",
      user_role: doctor_info?.["user_role"] || "",
    },

    enableReinitialize: true,
    validationSchema: validationSchema,
    validateOnChange: false,
    validate: async (values) => {
      const excludeContext = buildStaffExcludeContext(doctor_info);
      return validateOrgTeamUserFields(values, excludeContext, [
        ["aadhar", "mobile"],
        ["email", "registrationNumber"],
      ]);
    },
    onSubmit: async (values, { setFieldError, setFieldTouched }) => {
      const excludeContext = buildStaffExcludeContext(doctor_info);
      const duplicateErrors = await validateOrgTeamUserFields(values, excludeContext, [
        ["aadhar", "mobile"],
        ["email", "registrationNumber"],
      ]);
      if (Object.keys(duplicateErrors).length > 0) {
        Object.entries(duplicateErrors).forEach(([field, message]) => {
          setFieldError(field, message);
          setFieldTouched(field, true, false);
        });
        return;
      }

      const req_Data = {
        ...values,
        joiningDate: format(new Date(values.joiningDate), "yyyy-MM-dd"),
      };

      try {
        if (!!doctor_info) {
          const data = await updateDoctor(doctor_info.id, req_Data);
        } else {
          const data = await createDoctor(req_Data);
        }
        handelCloseModalState();
      } catch (error) {
        console.log(error);
      }
    },
  });

  const handelCloseModalState = () => {
    onClose();
    formik.resetForm();
  };

  return (
    <BasicModal
      open={open}
      onClose={handelCloseModalState}
      sx={LARGE_FORM_MODAL_SX}
      title={!!doctor_info ? "Update Staff" : "Add New Staff"}
    >
      <Box display={"flex"} flexDirection={"column"} >
        <Box>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="name"
                  name="name"
                  label="Name"
                  value={formik.values.name}
                  onChange={(e) => {
                    let inputValue = e.target.value;
                    
                    // Capitalize first letter
                    if (inputValue.length > 0) {
                      inputValue = inputValue.charAt(0).toUpperCase() + inputValue.slice(1);
                    }
                    
                    formik.setFieldValue("name", inputValue);
                  }}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label="Email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="education"
                  name="education"
                  label="Education"
                  value={formik.values.education}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.education && Boolean(formik.errors.education)
                  }
                  helperText={
                    formik.touched.education && formik.errors.education
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="specialty"
                  name="specialty"
                  label="Specialty"
                  value={formik.values.specialty}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.specialty && Boolean(formik.errors.specialty)
                  }
                  helperText={
                    formik.touched.specialty && formik.errors.specialty
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="aadhar"
                  name="aadhar"
                  label="Aadhar"
                  value={formik.values.aadhar}
                  onChange={formik.handleChange}
                  error={formik.touched.aadhar && Boolean(formik.errors.aadhar)}
                  helperText={formik.touched.aadhar && formik.errors.aadhar}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="mobile"
                  name="mobile"
                  label="Contact Number"
                  value={formik.values.mobile}
                  onChange={formik.handleChange}
                  error={formik.touched.mobile && Boolean(formik.errors.mobile)}
                  helperText={formik.touched.mobile && formik.errors.mobile}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="registrationNumber"
                  name="registrationNumber"
                  label="Registration Number"
                  value={formik.values.registrationNumber}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.registrationNumber &&
                    Boolean(formik.errors.registrationNumber)
                  }
                  helperText={
                    formik.touched.registrationNumber &&
                    formik.errors.registrationNumber
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  id="departmentId"
                  name="departmentId"
                  fullWidth
                  select
                  label="Department"
                  value={formik.values.departmentId}
                  onChange={async (e) => {
                    formik.setFieldValue("departmentId", e.target.value);
                  }}
                  error={
                    formik.touched.departmentId &&
                    Boolean(formik.errors.departmentId)
                  }
                  helperText={
                    formik.touched.departmentId && formik.errors.departmentId
                  }
                >
                  {visibleDepartments.map((departmentId) => (
                    <MenuItem
                      key={departmentId["id"]}
                      value={departmentId["id"]}
                    >
                      {departmentId["name"]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Joining Date"
                    format="YYYY-MM-DD"
                    value={formik.values.joiningDate}
                    onChange={(value) =>
                      formik.setFieldValue("joiningDate", value)
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error:
                          formik.touched.joiningDate &&
                          Boolean(formik.errors.joiningDate),
                        helperText:
                          formik.touched.joiningDate &&
                          formik.errors.joiningDate,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="address"
                  name="address"
                  label="Address"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.address && Boolean(formik.errors.address)
                  }
                  helperText={formik.touched.address && formik.errors.address}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="password"
                  name="password"
                  label="Password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.password && Boolean(formik.errors.password)
                  }
                  helperText={formik.touched.password && formik.errors.password}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  id="departmentId"
                  name="departmentId"
                  fullWidth
                  select
                  label="Department"
                  value={formik.values.departmentId}
                  onChange={async (e) => {
                    formik.setFieldValue("departmentId", e.target.value);
                  }}
                  error={
                    formik.touched.departmentId &&
                    Boolean(formik.errors.departmentId)
                  }
                  helperText={
                    formik.touched.departmentId && formik.errors.departmentId
                  }
                >
                  {visibleDepartments.map((departmentId) => (
                    <MenuItem
                      key={departmentId["id"]}
                      value={departmentId["id"]}
                    >
                      {departmentId["name"]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <FormModalActions
                  onCancel={handelCloseModalState}
                  onSubmit={async () => {
                    const errors = await formik.validateForm();
                    if (Object.keys(errors).length > 0) {
                      touchFormikErrorFields(formik, errors);
                      return;
                    }
                    formik.submitForm();
                  }}
                />
              </Grid>
            </Grid>
          </form>
        </Box>
      </Box>
    </BasicModal>
  );
};


export default AddNewDoctor;